import { motion } from 'framer-motion';
import { type CustomAdventure } from '@/utils/businessAppApi';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Calendar, Sparkles, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  formatCurrency, 
  getAdventureEmoji, 
  getAdventureDuration, 
  getAdventureCharacteristics,
  generateAdventureGradient 
} from '@/utils/adventureUtils';

interface AdventureCardProps {
  adventure: CustomAdventure;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * Enhanced Adventure Card Component with Professional Design
 * 
 * Displays an adventure with its key information, pricing, and characteristics.
 * Includes selection state, hover effects, and expandable details when selected.
 */
export const AdventureCard = ({ 
  adventure, 
  isSelected, 
  onClick
}: AdventureCardProps) => {
  const emoji = getAdventureEmoji(adventure);
  const duration = getAdventureDuration(adventure);
  const colorScheme = generateAdventureGradient(adventure.name);
  const characteristics = getAdventureCharacteristics(adventure);
  
  return (
    <Card 
      className={cn(
        "group overflow-hidden transition-all duration-300 cursor-pointer border-0 shadow-sm hover:shadow-lg w-full max-w-xs mx-auto",
        "transform hover:scale-[1.02] active:scale-[0.98]",
        isSelected 
          ? 'ring-2 ring-primary shadow-lg scale-[1.02] bg-gradient-to-r from-emerald-50 to-emerald-50' 
          : 'hover:shadow-md bg-white'
      )}
      onClick={onClick}
    >
      <CardContent className="p-0">
        {/* Hero Section with Image or Gradient */}
        <div className="relative">
          {adventure.images && adventure.images.length > 0 ? (
            // Image version
            <div className="relative h-20 overflow-hidden">
              <img
                src={adventure.images[0]}
                alt={adventure.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/50"></div>
              
              {/* Emoji overlay on image */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-2xl md:text-3xl drop-shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                  {emoji}
                </div>
              </div>
              
              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full p-1">
                  <Check className="h-3 w-3 text-primary" />
                </div>
              )}
            </div>
          ) : (
            // Gradient fallback version
            <div className={cn(
              "h-20 bg-gradient-to-br", 
              colorScheme.from, 
              colorScheme.to,
              "flex items-center justify-center relative overflow-hidden"
            )}>
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-white/10 opacity-30">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
                                   radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
                }} />
              </div>
              
              {/* Emoji with enhanced styling */}
              <div className="relative z-10 text-2xl md:text-3xl drop-shadow-sm transform group-hover:scale-110 transition-transform duration-300">
                {emoji}
              </div>
              
              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full p-1">
                  <Check className="h-3 w-3 text-primary" />
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Content Section */}
        <div className="p-4 space-y-3">
          {/* Title and Price */}
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-base text-gray-900 leading-tight line-clamp-2 flex-1">
                {adventure.name}
              </h4>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-1">
                <span className="text-sm text-gray-500">From</span>
                <span className="text-lg font-bold text-primary">
                  {formatCurrency(adventure.basePrice, adventure.currency)}
                </span>
              </div>
              <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600 border-0">
                {adventure.options.length} options
              </Badge>
            </div>
          </div>
          
          {/* Characteristics Tags */}
          {characteristics.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {characteristics.map((char, index) => (
                <div
                  key={index}
                  className={cn(
                    "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                    colorScheme.bg,
                    colorScheme.text,
                    "border border-current/20"
                  )}
                >
                  <span className="text-xs">{char.icon}</span>
                  <span>{char.label}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Description preview - always visible but compact */}
          <div className="pt-1">
            <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
              {adventure.description}
            </p>
          </div>
          
          {/* Expanded content when selected */}
          {isSelected && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-gray-100 pt-3 mt-3"
            >
              <div className="space-y-2">
                {/* Additional details */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Calendar className="h-3 w-3" />
                    <span>Flexible dates</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Sparkles className="h-3 w-3" />
                    <span>Customizable</span>
                  </div>
                </div>
                
                {/* Action hint */}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-gray-500">Selected for customization</span>
                  <ChevronRight className="h-4 w-4 text-primary animate-pulse" />
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
